// src/pages/CuotasExtraordinarias.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, onSnapshot, doc, collectionGroup, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
    FaArrowLeft,
    FaFilePdf,
    FaCheckCircle,
    FaExclamationCircle,
    FaCoins,
    FaEdit,
    FaUsers,
    FaClock,
    FaPlus,
    FaChartBar,
    FaTimesCircle,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { esCasaCaseta } from '../utils/morosoUtils';
import ModalRegistroPago from '../components/ModalRegistroPago';
import './CuotasExtraordinarias.css';

// ── Utilidades ──────────────────────────────────────────────────────────────
const fmtCurrency = (n) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

const daysSince = (fecha) => {
    if (!fecha) return null;
    const d = fecha?.toDate ? fecha.toDate() : new Date(fecha);
    const diff = Date.now() - d.getTime();
    return Math.floor(diff / 86400000);
};

const CONCEPT_ICONS = ['🎨', '🔧', '🏊', '🌿', '🔒', '💡', '🚗', '🏗️'];

// ── Componente ───────────────────────────────────────────────────────────────
const CuotasExtraordinarias = () => {
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;
    const navigate = useNavigate();

    const [cuotasConfig, setCuotasConfig] = useState([]);
    const [fraccConfig, setFraccConfig] = useState(null);
    const [casas, setCasas] = useState([]);
    const [mesesPendientes, setMesesPendientes] = useState([]);
    const [selectedCuota, setSelectedCuota] = useState(null);
    const [showPDFModal, setShowPDFModal] = useState(false);
    const [pdfCuotaTarget, setPdfCuotaTarget] = useState(null); // null = global, obj = concepto
    const [filterCuotaDescripcion, setFilterCuotaDescripcion] = useState('ALL');
    const [currentPagePending, setCurrentPagePending] = useState(1);
    const [desglosePages, setDesglosePages] = useState({});
    const [soloPendientesPorCuota, setSoloPendientesPorCuota] = useState({});
    const [casaParaPago, setCasaParaPago] = useState(null);

    // ── Handlers ─────────────────────────────────────────────────────────────

    // ── Firestore subscriptions ──────────────────────────────────────────────
    useEffect(() => {
        if (!fraccionamientoId) return;

        const unsubConfig = onSnapshot(doc(db, 'fraccionamientos', fraccionamientoId), (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                setFraccConfig(data);
                setCuotasConfig(data.configuracionFinanciera?.cuotaExtras || []);
            }
        });

        const unsubCasas = onSnapshot(
            collection(db, 'fraccionamientos', fraccionamientoId, 'casas'),
            (snap) => setCasas(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        );

        const unsubMeses = onSnapshot(
            query(collectionGroup(db, 'mesesPendientes'), where('fraccionamientoId', '==', String(fraccionamientoId))),
            (snap) => {
                setMesesPendientes(snap.docs.map((d) => {
                    const data = d.data() || {};
                    // Obtener casaId del path del documento de forma segura como fallback
                    const casaId = data.casaId || d.ref.parent?.parent?.id;
                    return { id: d.id, casaId, ...data };
                }));
            },
            (error) => {
                console.error('🔴 [Firestore] Error en Collection Group:', error);
                // Si falta el índice, el mensaje de error contendrá el link
                if (error.message?.includes('index')) {
                    console.warn('⚠️ Se requiere un índice de Collection Group. Revisa el enlace en la consola.');
                }
            }
        );

        return () => { unsubConfig(); unsubCasas(); unsubMeses(); };
    }, [fraccionamientoId]);

    // ── Helpers ──────────────────────────────────────────────────────────────
    const isResidentOnly = useCallback((casa) => {
        if (!casa) return false;
        if (esCasaCaseta(casa)) return false;
        const rol = (casa.rol || casa.rolAsignado || '').toLowerCase();
        const inclusionRoles = ['user', 'residente', 'propietario', 'arrendatario', ''];
        return inclusionRoles.includes(rol);
    }, []);

    const normalizeConceptName = useCallback((conceptoRaw) => {
        if (!conceptoRaw) return '';
        let clean = String(conceptoRaw)
            .replace(/^cuota\s+extra(ordinaria)?\s*:\s*/i, '')
            .replace(/^cuota\s+extra(ordinaria)?\s+/i, '')
            .trim();
        if (clean.toUpperCase() === 'ALBERCA') {
            clean = 'ALBERCA';
        }
        return clean;
    }, []);

    const isDocPaid = useCallback((doc) => {
        if (!doc) return false;
        if (doc.estado === 'pagado_totalmente' || doc.estado === 'pagado_completo') return true;
        return doc.montoPendiente !== undefined && doc.montoPendiente !== null && Number(doc.montoPendiente) <= 0;
    }, []);

    // ── Descubrimiento inteligente de cuotas extraordinarias ───────────────────
    const allExtraordinaryConcepts = useMemo(() => {
        const conceptMap = new Map();

        // 1. Agregar cuotas desde la configuración financiera (excluyendo ANUALIDADES)
        (cuotasConfig || []).forEach((cfg) => {
            const desc = (cfg.descripcion || '').trim();
            const norm = normalizeConceptName(desc);
            if (!norm || norm.toUpperCase() === 'ANUALIDADES') return;

            conceptMap.set(norm, {
                ...cfg,
                id: cfg.id || `CONFIG-${norm}`,
                descripcion: norm,
                monto: Number(cfg.monto) || 0,
                periodicidad: cfg.periodicidad || cfg.frecuencia || 'Pago Único',
                origen: 'config'
            });
        });

        // 2. Descubrir cuotas existentes desde mesesPendientes (documentos con EXT- o tipo cuota_extraordinaria)
        (mesesPendientes || []).forEach((doc) => {
            const isExt = String(doc.id || '').startsWith('EXT-') || doc.tipo === 'cuota_extraordinaria';
            if (!isExt) return;

            const norm = normalizeConceptName(doc.concepto);
            if (!norm || norm.toUpperCase() === 'ANUALIDADES') return;

            if (!conceptMap.has(norm)) {
                const isAnual = /anual/i.test(norm);
                conceptMap.set(norm, {
                    id: `EXT-AUTO-${norm}`,
                    descripcion: norm,
                    monto: Number(doc.monto) || 0,
                    periodicidad: isAnual ? 'Anual' : 'Pago Único',
                    origen: 'mesesPendientes'
                });
            } else {
                const existing = conceptMap.get(norm);
                if ((!existing.monto || existing.monto <= 0) && Number(doc.monto) > 0) {
                    existing.monto = Number(doc.monto);
                }
            }
        });

        const list = Array.from(conceptMap.values());

        // 3. Ordenar: ALBERCA primero, luego anualidades en orden descendente, y el resto por nombre
        return list.sort((a, b) => {
            if (a.descripcion.toUpperCase() === 'ALBERCA') return -1;
            if (b.descripcion.toUpperCase() === 'ALBERCA') return 1;

            const matchA = a.descripcion.match(/\d{4}/);
            const matchB = b.descripcion.match(/\d{4}/);
            if (matchA && matchB) {
                const yA = parseInt(matchA[0], 10);
                const yB = parseInt(matchB[0], 10);
                if (yA !== yB) return yB - yA;
            }
            return a.descripcion.localeCompare(b.descripcion, 'es', { numeric: true });
        });
    }, [cuotasConfig, mesesPendientes, normalizeConceptName]);

    // ── Derived data ─────────────────────────────────────────────────────────
    const cuotasStats = useMemo(() => {
        return allExtraordinaryConcepts.map((config, idx) => {
            const normTarget = normalizeConceptName(config.descripcion).toLowerCase();

            const docs = mesesPendientes.filter((d) => {
                const isExt = String(d.id || '').startsWith('EXT-') || d.tipo === 'cuota_extraordinaria';
                if (!isExt) return false;

                const normDocConcept = normalizeConceptName(d.concepto).toLowerCase();
                const matchesConcept = normDocConcept === normTarget ||
                    d.concepto?.toLowerCase().includes(normTarget) ||
                    (config.id && d.cuotaExtraId === config.id);

                const casaRef = casas.find((c) => c.id === d.casaId);
                return matchesConcept && isResidentOnly(casaRef);
            });

            const paidDocs = docs.filter(isDocPaid);
            const pendingDocs = docs.filter((d) => !isDocPaid(d));

            const totalPaid = docs.reduce((sum, d) => {
                const isPaid = isDocPaid(d);
                const total = Number(d.monto) || Number(config.monto) || 0;
                const pendiente = Number(d.montoPendiente) || 0;
                if (isPaid) {
                    return sum + total;
                }
                return sum + Math.max(0, total - pendiente);
            }, 0);

            const totalPending = docs.reduce((sum, d) => {
                if (isDocPaid(d)) return sum;
                return sum + (Number(d.montoPendiente) !== undefined && Number(d.montoPendiente) > 0
                    ? Number(d.montoPendiente)
                    : (Number(d.monto) || Number(config.monto) || 0));
            }, 0);

            const totalExpected = totalPaid + totalPending;
            const progress = totalExpected > 0 ? (totalPaid / totalExpected) * 100 : 0;

            const totalResidentsInConcept = (config.origen === 'config' || docs.length >= casas.filter(isResidentOnly).length)
                ? casas.filter(isResidentOnly).length
                : docs.length;

            return {
                ...config,
                idx,
                docs,
                paidDocs,
                pendingDocs,
                totalExpected,
                totalPaid,
                totalPending,
                progress,
                paidCount: paidDocs.length,
                pendingCount: pendingDocs.length,
                totalResidents: totalResidentsInConcept,
                periodicidad: config.periodicidad || 'Pago Único',
                icon: CONCEPT_ICONS[idx % CONCEPT_ICONS.length],
            };
        });
    }, [allExtraordinaryConcepts, mesesPendientes, casas, isResidentOnly, isDocPaid, normalizeConceptName]);

    // ── Stats filtradas según el selector de cuota ───────────────────────────
    const filteredCuotasStats = useMemo(() => {
        if (filterCuotaDescripcion === 'ALL') return cuotasStats;
        return cuotasStats.filter(c => c.descripcion === filterCuotaDescripcion);
    }, [cuotasStats, filterCuotaDescripcion]);

    // ── Global metrics basadas en filtro ──────────────────────────────────────
    const globalMetrics = useMemo(() => {
        const statsToCalculate = filteredCuotasStats;
        const totalRecaudado = statsToCalculate.reduce((s, c) => s + c.totalPaid, 0);
        const totalPorCobrar = statsToCalculate.reduce((s, c) => s + c.totalPending, 0);
        const pendingResidents = new Set(
            statsToCalculate.flatMap((c) => c.pendingDocs.map((d) => d.casaId))
        ).size;
        return {
            cuotasActivas: statsToCalculate.length,
            pendingResidents,
            totalRecaudado,
            totalPorCobrar,
        };
    }, [filteredCuotasStats]);

    // ── All pending residents (cross-cuota) ──────────────────────────────────
    const allPendingResidents = useMemo(() => {
        const map = {};
        cuotasStats.forEach((cuota) => {
            cuota.pendingDocs.forEach((doc) => {
                const casa = casas.find((c) => c.id === doc.casaId);
                if (!casa || !isResidentOnly(casa)) return;
                if (!map[casa.id]) map[casa.id] = { casa, concepts: [] };
                map[casa.id].concepts.push({
                    concepto: cuota.descripcion,
                    monto: cuota.monto,
                    dias: daysSince(doc.fechaCreacion || doc.timestamp),
                    estado: doc.estado,
                });
            });
        });
        return Object.values(map);
    }, [cuotasStats, casas, isResidentOnly]);

    // ── Pendientes filtrados por el selector de cuota ─────────────────────────
    const filteredPendingResidents = useMemo(() => {
        if (filterCuotaDescripcion === 'ALL') return allPendingResidents;
        return allPendingResidents.filter(({ concepts }) =>
            concepts.some((c) => c.concepto === filterCuotaDescripcion)
        );
    }, [allPendingResidents, filterCuotaDescripcion]);

    // ── PDF helpers ──────────────────────────────────────────────────────────
    const generatePDF = (cuota) => {
        const target = cuota || { descripcion: 'Todas las cuotas', allStats: cuotasStats };
        const stats = cuota ? [cuota] : cuotasStats;

        const residentsRows = casas
            .filter(isResidentOnly)
            .map((casa) => {
                const rows = [];
                stats.forEach((c) => {
                    const docExtra = c.docs?.find((d) => d.casaId === casa.id);
                    const isPaid = isDocPaid(docExtra);
                    const fechaPago = docExtra?.fechaPago || docExtra?.updatedAt;
                    const fechaVenc = docExtra?.fechaCreacion || docExtra?.timestamp;
                    const montoTotal = docExtra?.monto || c.monto || 0;
                    const pagado = isPaid ? montoTotal : (montoTotal - (docExtra?.montoPendiente || 0));

                    rows.push({
                        concepto: c.descripcion,
                        numero: casa.numero,
                        propietario: casa.propietario || casa.nombre_residente || 'Sin asignar',
                        status: isPaid ? 'Pagado' : 'Pendiente',
                        periodicidad: c.periodicidad || 'Pago Único',
                        monto: montoTotal,
                        pagado: pagado,
                        fechaPago: isPaid && fechaPago
                            ? (fechaPago.toDate ? fechaPago.toDate() : new Date(fechaPago)).toLocaleDateString('es-MX')
                            : '—',
                        diasVencido: !isPaid ? daysSince(fechaVenc) : null,
                    });
                });
                return rows;
            })
            .flat()
            .sort((a, b) => (a.status === 'Pendiente' ? -1 : 1));

        const pendientes = residentsRows.filter((r) => r.status === 'Pendiente');
        const pagados = residentsRows.filter((r) => r.status === 'Pagado');

        const configRecibos = fraccConfig?.configuracionRecibos || {};
        const condominioNombre = configRecibos.condominioNombre || fraccConfig?.condominioNombre || fraccConfig?.nombre || userData?.fraccionamientoNombre || 'Fraccionamiento';
        const logoUrl = configRecibos.logoUrl || fraccConfig?.logoUrl || userData?.fraccionamientoLogoUrl || userData?.logoUrl || null;
        const nowStr = new Date().toLocaleString('es-MX', { timeZone: 'America/Mexico_City', dateStyle: 'medium', timeStyle: 'medium' });

        const totalPaidAll = stats.reduce((s, c) => s + c.totalPaid, 0);
        const totalPendingAll = stats.reduce((s, c) => s + c.totalPending, 0);
        const totalExpectedAll = totalPaidAll + totalPendingAll;
        const porcentajePagado = totalExpectedAll > 0 ? Math.round((totalPaidAll / totalExpectedAll) * 100) : 0;

        const w = window.open('', '_blank');
        w.document.write(`
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8"/>
    <title>Reporte de Cuotas Extraordinarias${cuota ? ` – ${cuota.descripcion}` : ''}</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #0f172a; background: #ffffff; padding: 32px; font-size: 12px; }
        .header-container { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #f59e0b; padding-bottom: 16px; margin-bottom: 24px; }
        .header-left { display: flex; align-items: center; gap: 16px; }
        .logo-img { height: 60px; max-width: 180px; object-fit: contain; }
        .condo-name { font-size: 20px; font-weight: 800; color: #d97706; text-transform: uppercase; letter-spacing: -0.02em; }
        .report-title { font-size: 13px; font-weight: 600; color: #475569; margin-top: 2px; }
        .timestamp-card { background: #f8fafc; border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px 14px; text-align: right; }
        .ts-label { font-size: 8px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
        .ts-value { font-size: 11px; font-weight: 700; color: #0f172a; margin-top: 2px; }
        .ts-sub { font-size: 9px; color: #d97706; font-weight: 600; margin-top: 1px; }

        .dashboard-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 16px; margin-bottom: 24px; align-items: center; }
        .metrics-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        .metric-box { border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; background: #f8fafc; text-align: center; }
        .metric-box .label { font-size: 9px; text-transform: uppercase; color: #64748b; font-weight: 700; letter-spacing: 0.03em; }
        .metric-box .val { font-size: 17px; font-weight: 800; margin-top: 4px; }
        .val.green { color: #16a34a; }
        .val.amber { color: #d97706; }
        .val.neutral { color: #0f172a; }

        .chart-box { border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; background: #ffffff; display: flex; align-items: center; justify-content: space-around; }
        .donut-chart { width: 80px; height: 80px; transform: rotate(-90deg); border-radius: 50%; }
        .chart-legend { font-size: 10px; }
        .legend-item { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
        .dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
        .dot.green { background: #22c55e; }
        .dot.amber { background: #f59e0b; }

        h2 { font-size: 13px; font-weight: 700; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; color: #0f172a; text-transform: uppercase; }
        .section { margin-bottom: 24px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 8px; }
        th { text-align: left; padding: 8px 10px; background: #0f172a; color: #ffffff; font-weight: 700; font-size: 9px; text-transform: uppercase; }
        td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
        tr:nth-child(even) td { background-color: #f8fafc; }
        .badge { display: inline-block; padding: 3px 8px; border-radius: 99px; font-size: 9px; font-weight: 800; letter-spacing: 0.03em; }
        .badge-green { background: #d1fae5; color: #065f46; }
        .badge-amber { background: #fef3c7; color: #92400e; }
        .footer { margin-top: 32px; text-align: center; color: #94a3b8; font-size: 10px; border-top: 1px solid #e2e8f0; padding-top: 12px; }
        @media print { body { padding: 16px; } }
    </style>
</head>
<body>
    <div class="header-container">
        <div class="header-left">
            ${logoUrl ? `<img src="${logoUrl}" alt="Logo" class="logo-img" />` : ''}
            <div>
                <div class="condo-name">${condominioNombre}</div>
                <div class="report-title">Reporte de Auditoría – Cuotas Extraordinarias${cuota ? `: ${cuota.descripcion}` : ''}</div>
            </div>
        </div>
        <div class="timestamp-card">
            <div class="ts-label">Fecha y Hora de Descarga</div>
            <div class="ts-value">${nowStr}</div>
            <div class="ts-sub">Tiempo del Centro de México (CST)</div>
        </div>
    </div>

    <div class="dashboard-grid">
        <div class="metrics-row">
            <div class="metric-box">
                <div class="label">Total Recaudado</div>
                <div class="val green">${fmtCurrency(totalPaidAll)}</div>
            </div>
            <div class="metric-box">
                <div class="label">Total Pendiente</div>
                <div class="val amber">${fmtCurrency(totalPendingAll)}</div>
            </div>
            <div class="metric-box">
                <div class="label">Residentes con Adeudo</div>
                <div class="val neutral">${pendientes.length}</div>
            </div>
        </div>
        <div class="chart-box">
            <svg class="donut-chart" viewBox="0 0 36 36">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#fef3c7" stroke-width="4.5" />
                <path stroke-dasharray="${porcentajePagado}, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#16a34a" stroke-width="4.5" />
            </svg>
            <div class="chart-legend">
                <div style="font-weight: 800; font-size: 14px; color: #0f172a; margin-bottom: 4px;">${porcentajePagado}% Recaudado</div>
                <div class="legend-item"><span class="dot green"></span> <strong>${pagados.length}</strong> Pagos liquidados</div>
                <div class="legend-item"><span class="dot amber"></span> <strong>${pendientes.length}</strong> Adeudos activos</div>
            </div>
        </div>
    </div>

    <div class="section">
        <h2>🔴 Pendientes de Pago (${pendientes.length})</h2>
        <table>
            <thead>
                <tr>
                    <th>Casa</th>
                    <th>Residente</th>
                    <th>Concepto</th>
                    <th>Periodicidad</th>
                    <th>Monto Total</th>
                    <th>Días sin pagar</th>
                    <th>Estado</th>
                </tr>
            </thead>
            <tbody>
                ${pendientes.length === 0
                    ? '<tr><td colspan="7" style="text-align:center;color:#9ca3af;padding:20px">Sin pendientes</td></tr>'
                    : pendientes.map((r) => `
                        <tr>
                            <td><strong>Casa ${r.numero}</strong></td>
                            <td>${r.propietario}</td>
                            <td>${r.concepto}</td>
                            <td>${r.periodicidad}</td>
                            <td><strong>${fmtCurrency(r.monto)}</strong></td>
                            <td>${r.diasVencido !== null ? `${r.diasVencido} días` : '—'}</td>
                            <td><span class="badge badge-amber">PENDIENTE</span></td>
                        </tr>`).join('')}
            </tbody>
        </table>
    </div>

    <div class="section">
        <h2>✅ Pagos Realizados (${pagados.length})</h2>
        <table>
            <thead>
                <tr>
                    <th>Casa</th>
                    <th>Residente</th>
                    <th>Concepto</th>
                    <th>Periodicidad</th>
                    <th>Pagado</th>
                    <th>Fecha de Pago</th>
                    <th>Estado</th>
                </tr>
            </thead>
            <tbody>
                ${pagados.length === 0
                    ? '<tr><td colspan="7" style="text-align:center;color:#9ca3af;padding:20px">Sin pagos registrados</td></tr>'
                    : pagados.map((r) => `
                        <tr>
                            <td><strong>Casa ${r.numero}</strong></td>
                            <td>${r.propietario}</td>
                            <td>${r.concepto}</td>
                            <td>${r.periodicidad}</td>
                            <td><strong>${fmtCurrency(r.pagado)}</strong></td>
                            <td>${r.fechaPago}</td>
                            <td><span class="badge badge-green">PAGADO</span></td>
                        </tr>`).join('')}
            </tbody>
        </table>
    </div>

    <div class="footer">
        Reporte oficial de auditoría de cuotas extraordinarias generado por Domus Hábitat para ${condominioNombre}.
    </div>

    <script>
        window.onload = () => setTimeout(() => window.print(), 600);
    </script>
</body>
</html>`);
        w.document.close();
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="ceq-page">

            {/* ── Header Envolvente ── */}
            <div className="ceq-header">
                <div className="ceq-header-left">
                    <div className="ceq-header-icon">
                        <FaCoins />
                    </div>
                    <div className="ceq-header-title-block">
                        <h1 className="ceq-title">Cuotas Extraordinarias</h1>
                        <p className="ceq-subtitle">Control de fondos especiales y recaudación por concepto</p>
                    </div>
                </div>
                <div className="ceq-header-right-actions">
                    <button className="ceq-btn-ghost" onClick={() => generatePDF(null)}>
                        <FaFilePdf /> Reporte PDF
                    </button>
                    <button
                        className="ceq-btn-soft"
                        onClick={() => navigate('/configuraciones/financiera')}
                    >
                        <FaPlus /> Nueva cuota
                    </button>
                    <button className="ceq-btn-back" onClick={() => navigate('/administracion')}>
                        <FaArrowLeft /> Regresar a administración
                    </button>
                </div>
            </div>

            {/* ── Selector de Cuota Extraordinaria ── */}
            {allExtraordinaryConcepts.length > 0 && (
                <div className="ceq-selector-card" style={{ background: '#ffffff', border: '1px solid #e8e4dd', borderRadius: '14px', padding: '16px 20px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1a1a1a' }}>
                            Seleccionar Cuota Extraordinaria:
                        </label>
                        <select
                            value={filterCuotaDescripcion}
                            onChange={(e) => {
                                setFilterCuotaDescripcion(e.target.value);
                                setCurrentPagePending(1);
                            }}
                            style={{ flex: 1, minWidth: '240px', padding: '9px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.92rem', fontWeight: '600', color: '#0f172a', background: '#f8fafc', cursor: 'pointer' }}
                        >
                            <option value="ALL">Todas las cuotas extraordinarias ({allExtraordinaryConcepts.length} activas)</option>
                            {allExtraordinaryConcepts.map(c => (
                                <option key={c.descripcion} value={c.descripcion}>
                                    {c.descripcion} ({fmtCurrency(c.monto)} - {c.periodicidad || 'Pago Único'})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* ── Global Metric Cards ── */}
            <div className="ceq-metrics-grid">
                <div className="ceq-metric-card">
                    <span className="ceq-metric-label">CUOTAS ACTIVAS</span>
                    <span className="ceq-metric-value ceq-metric-neutral">{globalMetrics.cuotasActivas}</span>
                    <span className="ceq-metric-desc">
                        {globalMetrics.cuotasActivas === 1 ? 'concepto configurado' : 'conceptos configurados'}
                    </span>
                </div>
                <div className="ceq-metric-card">
                    <span className="ceq-metric-label">RESIDENTES PENDIENTES</span>
                    <span className="ceq-metric-value ceq-metric-amber">{globalMetrics.pendingResidents}</span>
                    <span className="ceq-metric-desc">
                        {globalMetrics.pendingResidents === 0 ? 'sin pago registrado' : 'con adeudo activo'}
                    </span>
                </div>
                <div className="ceq-metric-card">
                    <span className="ceq-metric-label">TOTAL RECAUDADO</span>
                    <span className="ceq-metric-value ceq-metric-green">{fmtCurrency(globalMetrics.totalRecaudado)}</span>
                    <span className="ceq-metric-desc">de conceptos seleccionados</span>
                </div>
                <div className="ceq-metric-card">
                    <span className="ceq-metric-label">TOTAL PENDIENTE</span>
                    <span className="ceq-metric-value ceq-metric-amber">{fmtCurrency(globalMetrics.totalPorCobrar)}</span>
                    <span className="ceq-metric-desc">por cobrar</span>
                </div>
            </div>

            {/* ── Concepts Section ── */}
            <div className="ceq-section">
                <div className="ceq-section-header">
                    <FaChartBar className="ceq-section-icon" />
                    <h2 className="ceq-section-title">Conceptos registrados</h2>
                    <span className="ceq-badge">{filteredCuotasStats.length} {filteredCuotasStats.length === 1 ? 'concepto' : 'conceptos'}</span>
                </div>

                {filteredCuotasStats.length === 0 ? (
                    <div className="ceq-empty-state">
                        <FaCoins className="ceq-empty-icon" />
                        <p>No hay cuotas extraordinarias que coincidan con la selección.</p>
                        <button className="ceq-btn-primary" onClick={() => setFilterCuotaDescripcion('ALL')}>
                            Ver todas las cuotas
                        </button>
                    </div>
                ) : (
                    <div className="ceq-concepts-list">
                        {filteredCuotasStats.map((cuota) => (
                            <div
                                key={cuota.descripcion}
                                className={`ceq-concept-card ${selectedCuota?.descripcion === cuota.descripcion ? 'is-selected' : ''}`}
                            >
                                {/* Card top row */}
                                <div className="ceq-concept-top">
                                    <div className="ceq-concept-identity">
                                        <div className="ceq-concept-emoji">{cuota.icon}</div>
                                        <div>
                                            <div className="ceq-concept-name">{cuota.descripcion}</div>
                                            <div className="ceq-concept-unit">
                                                Cuota unitaria: {fmtCurrency(cuota.monto)} por casa
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ceq-concept-period-badge">
                                        <span>{cuota.periodicidad}</span>
                                    </div>
                                    <div className="ceq-concept-actions">
                                        <button
                                            className="ceq-btn-icon"
                                            title="Editar en configuración"
                                            onClick={() => navigate('/configuraciones/financiera')}
                                        >
                                            <FaEdit /> Editar
                                        </button>
                                        <button
                                            className="ceq-btn-icon ceq-btn-pdf"
                                            title="Descargar PDF de este concepto"
                                            onClick={() => generatePDF(cuota)}
                                        >
                                            <FaFilePdf /> PDF
                                        </button>
                                    </div>
                                </div>

                                {/* Progress bar */}
                                <div className="ceq-progress-row">
                                    <span className="ceq-progress-label">
                                        Recaudación {Math.round(cuota.progress || 0)}%
                                    </span>
                                    <span className="ceq-progress-amounts">
                                        {fmtCurrency(cuota.totalPaid)} / {fmtCurrency(cuota.totalExpected)}
                                    </span>
                                </div>
                                <div className="ceq-progress-track">
                                    <div
                                        className="ceq-progress-fill"
                                        style={{ width: `${Math.min(cuota.progress || 0, 100)}%` }}
                                    />
                                </div>

                                {/* Metrics row */}
                                <div className="ceq-concept-metrics">
                                    <div className="ceq-concept-metric">
                                        <span className="ceq-m-label">Recaudado</span>
                                        <span className="ceq-m-value green">{fmtCurrency(cuota.totalPaid)}</span>
                                    </div>
                                    <div className="ceq-concept-metric">
                                        <span className="ceq-m-label">Pendiente</span>
                                        <span className="ceq-m-value amber">{fmtCurrency(cuota.totalPending)}</span>
                                    </div>
                                    <div className="ceq-concept-metric">
                                        <span className="ceq-m-label">Pagados / Pendientes</span>
                                        <span className="ceq-m-value neutral">
                                            <FaCheckCircle className="icon-green" /> <span className="ceq-m-paid-count">{cuota.paidCount} pagados</span>&nbsp;·&nbsp;
                                            <FaExclamationCircle className="icon-amber" /> <span className="ceq-m-pending-count">{cuota.pendingCount} pendientes</span>
                                        </span>
                                    </div>
                                </div>

                                {/* Toggle detail */}
                                <button
                                    className="ceq-detail-toggle"
                                    onClick={() =>
                                        setSelectedCuota(
                                            selectedCuota?.descripcion === cuota.descripcion ? null : cuota
                                        )
                                    }
                                >
                                    {selectedCuota?.descripcion === cuota.descripcion
                                        ? '▲ Ocultar desglose por residente'
                                        : '▼ Ver desglose por residente'}
                                </button>

                                {/* Resident detail table */}
                                {selectedCuota?.descripcion === cuota.descripcion && (() => {
                                    const residentCasas = casas.filter(isResidentOnly);
                                    const casasDeEstaCuota = (cuota.origen === 'config' || cuota.docs.length >= residentCasas.length)
                                        ? residentCasas
                                        : residentCasas.filter((c) => cuota.docs.some((d) => d.casaId === c.id));

                                    const soloPendientes = !!soloPendientesPorCuota[cuota.descripcion];
                                    const casasFiltradas = casasDeEstaCuota.filter((casa) => {
                                        if (!soloPendientes) return true;
                                        const docExtra = cuota.docs.find((d) => d.casaId === casa.id);
                                        return !isDocPaid(docExtra);
                                    });

                                    const itemsPerPage = 15;
                                    const totalCount = casasFiltradas.length;
                                    const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
                                    const currentPage = Math.min(desglosePages[cuota.descripcion] || 1, totalPages);
                                    const startIndex = (currentPage - 1) * itemsPerPage;
                                    const paginatedCasas = casasFiltradas.slice(startIndex, startIndex + itemsPerPage);

                                    return (
                                        <div className="ceq-resident-table-wrap">
                                            {/* Toolbar con checkbox de filtro y contador */}
                                            <div className="ceq-table-toolbar">
                                                <label className="ceq-filter-checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        className="ceq-checkbox-input"
                                                        checked={soloPendientes}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setSoloPendientesPorCuota((prev) => ({
                                                                ...prev,
                                                                [cuota.descripcion]: checked
                                                            }));
                                                            setDesglosePages((prev) => ({
                                                                ...prev,
                                                                [cuota.descripcion]: 1
                                                            }));
                                                        }}
                                                    />
                                                    <span>Mostrar solo pendientes de pago ({cuota.pendingCount})</span>
                                                </label>
                                                <span className="ceq-table-counter">
                                                    {totalCount} {totalCount === 1 ? 'vivienda listada' : 'viviendas listadas'}
                                                </span>
                                            </div>

                                            <table className="ceq-resident-table">
                                                <thead>
                                                    <tr>
                                                        <th>Unidad</th>
                                                        <th>Residente</th>
                                                        <th>Estado</th>
                                                        <th>Monto</th>
                                                        <th>Detalle</th>
                                                        <th>Acción</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedCasas.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="6" style={{ textAlign: 'center', padding: '24px', color: '#059669', fontWeight: '600' }}>
                                                                {soloPendientes
                                                                    ? '🎉 ¡Excelente! No hay residentes pendientes en esta cuota extraordinaria.'
                                                                    : 'Sin registros para mostrar.'}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        paginatedCasas.map((casa) => {
                                                            const docExtra = cuota.docs.find((d) => d.casaId === casa.id);
                                                            const isPaid = isDocPaid(docExtra);
                                                            const montoDoc = docExtra?.monto || cuota.monto || 0;
                                                            const montoPend = docExtra?.montoPendiente !== undefined ? docExtra.montoPendiente : (!isPaid ? (cuota.monto || 0) : 0);

                                                            return (
                                                                <tr key={casa.id}>
                                                                    <td><strong>Casa {casa.numero}</strong></td>
                                                                    <td className="ceq-td-name">
                                                                        {casa.propietario || casa.nombre_residente || 'Sin asignar'}
                                                                    </td>
                                                                    <td>
                                                                        <span className={`ceq-status-tag ${isPaid ? 'paid' : 'pending'}`}>
                                                                            {isPaid ? 'Pagado' : 'Pendiente'}
                                                                        </span>
                                                                    </td>
                                                                    <td><strong>{fmtCurrency(montoDoc)}</strong></td>
                                                                    <td>
                                                                        {montoPend > 0 ? (
                                                                            <span className="ceq-pending-amount">
                                                                                Falta: {fmtCurrency(montoPend)}
                                                                            </span>
                                                                        ) : isPaid ? (
                                                                            <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '600' }}>Liquidado</span>
                                                                        ) : (
                                                                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>—</span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            type="button"
                                                                            className="ceq-btn-pay-shortcut"
                                                                            onClick={() => setCasaParaPago(casa)}
                                                                            title={`Registrar pago para Casa ${casa.numero}`}
                                                                        >
                                                                            💳 Pagar
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </table>

                                            {/* Paginación de 15 datos */}
                                            {totalCount > itemsPerPage && (
                                                <div className="ceq-pagination-footer">
                                                    <span className="ceq-pagination-info">
                                                        Mostrando <strong>{startIndex + 1}</strong> a <strong>{Math.min(startIndex + itemsPerPage, totalCount)}</strong> de <strong>{totalCount}</strong>
                                                    </span>
                                                    <div className="ceq-pagination-controls">
                                                        <button
                                                            type="button"
                                                            className="ceq-pagination-btn"
                                                            disabled={currentPage <= 1}
                                                            onClick={() => setDesglosePages((prev) => ({
                                                                ...prev,
                                                                [cuota.descripcion]: Math.max(1, currentPage - 1)
                                                            }))}
                                                        >
                                                            Anterior
                                                        </button>
                                                        <span className="ceq-pagination-page-indicator">
                                                            Página {currentPage} de {totalPages}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="ceq-pagination-btn"
                                                            disabled={currentPage >= totalPages}
                                                            onClick={() => setDesglosePages((prev) => ({
                                                                ...prev,
                                                                [cuota.descripcion]: Math.min(totalPages, currentPage + 1)
                                                            }))}
                                                        >
                                                            Siguiente
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Pending Residents Section (Paginado) ── */}
            <div className="ceq-section">
                <div className="ceq-section-header">
                    <FaExclamationCircle className="ceq-section-icon ceq-icon-amber" />
                    <h2 className="ceq-section-title">Pendientes de pago</h2>
                    <span className="ceq-badge ceq-badge-amber">
                        {filteredPendingResidents.length} {filteredPendingResidents.length === 1 ? 'residente' : 'residentes'}
                    </span>
                </div>

                {filteredPendingResidents.length === 0 ? (
                    <div className="ceq-pending-empty">
                        <FaCheckCircle className="ceq-pending-empty-icon" />
                        <p>Sin residentes pendientes en este momento</p>
                        <span>Todos los residentes están al corriente con sus cuotas extraordinarias.</span>
                    </div>
                ) : (
                    <>
                        <div className="ceq-pending-list">
                            {(() => {
                                const itemsPerPagePending = 10;
                                const startPendingIndex = (currentPagePending - 1) * itemsPerPagePending;
                                const paginatedPending = filteredPendingResidents.slice(startPendingIndex, startPendingIndex + itemsPerPagePending);

                                return paginatedPending.map(({ casa, concepts }) => {
                                    const maxDias = Math.max(...concepts.map((c) => c.dias || 0));
                                    return (
                                        <div key={casa.id} className="ceq-pending-row">
                                            <div className="ceq-pending-identity">
                                                <div className="ceq-pending-casa">Casa {casa.numero}</div>
                                                <div className="ceq-pending-name">
                                                    {casa.propietario || casa.nombre_residente || 'Sin asignar'}
                                                </div>
                                            </div>
                                            <div className="ceq-pending-concepts">
                                                {concepts.map((c, i) => (
                                                    <span key={i} className="ceq-concept-chip">
                                                        {c.concepto} · {fmtCurrency(c.monto)}
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="ceq-pending-meta">
                                                {maxDias > 0 && (
                                                    <span className={`ceq-days-badge ${maxDias > 30 ? 'critical' : maxDias > 15 ? 'warning' : 'mild'}`}>
                                                        <FaClock /> {maxDias} días
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    className="ceq-btn-pay-shortcut"
                                                    onClick={() => setCasaParaPago(casa)}
                                                    title={`Registrar pago para Casa ${casa.numero}`}
                                                >
                                                    💳 Pagar
                                                </button>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>

                        {/* Bar de Paginación de Pendientes */}
                        {(() => {
                            const totalPendingCount = filteredPendingResidents.length;
                            const itemsPerPagePending = 10;
                            const totalPendingPages = Math.max(1, Math.ceil(totalPendingCount / itemsPerPagePending));
                            const startPendingIndex = (currentPagePending - 1) * itemsPerPagePending;
                            if (totalPendingCount <= 10) return null;

                            return (
                                <div className="pending-pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e8e4dd' }}>
                                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                        Mostrando <strong>{startPendingIndex + 1}</strong> a <strong>{Math.min(startPendingIndex + itemsPerPagePending, totalPendingCount)}</strong> de <strong>{totalPendingCount}</strong> residentes pendientes
                                    </span>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <button
                                            type="button"
                                            disabled={currentPagePending <= 1}
                                            onClick={() => setCurrentPagePending((prev) => Math.max(prev - 1, 1))}
                                            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: currentPagePending <= 1 ? '#f1f5f9' : '#ffffff', cursor: currentPagePending <= 1 ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: '600' }}
                                        >
                                            Anterior
                                        </button>
                                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>
                                            Página {currentPagePending} de {totalPendingPages}
                                        </span>
                                        <button
                                            type="button"
                                            disabled={currentPagePending >= totalPendingPages}
                                            onClick={() => setCurrentPagePending((prev) => Math.min(prev + 1, totalPendingPages))}
                                            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: currentPagePending >= totalPendingPages ? '#f1f5f9' : '#ffffff', cursor: currentPagePending >= totalPendingPages ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: '600' }}
                                        >
                                            Siguiente
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </>
                )}
            </div>

            {/* Modal de Registro de Pago rápido */}
            {casaParaPago && (
                <ModalRegistroPago
                    casa={casaParaPago}
                    onClose={() => setCasaParaPago(null)}
                    onConfirm={() => setCasaParaPago(null)}
                />
            )}
        </div>
    );
};

export default CuotasExtraordinarias;
